// src/HeygenAvatar/HeygenAvatar.js
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { Mic, MicOff } from "@mui/icons-material";
import { LiveAvatarSession } from "@heygen/liveavatar-web-sdk";
import { createLiveAvatarSessionToken } from "../api/heygenLiveAvatar";

/**
 * Simple LiveAvatar wrapper component.
 *
 * Usage:
 *   <HeygenAvatar />
 *
 * You must set REACT_APP_HEYGEN_API_KEY in a .env file at the project root:
 *   REACT_APP_HEYGEN_API_KEY=019098fd-12e8-11f1-a99e-066a7fa2e369
 *
 * Then run: npm start
 */
export default function HeygenAvatar() {
  const [isStarting, setIsStarting] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [textInput, setTextInput] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("Idle");

  const sessionRef = useRef(null);
  const videoRef = useRef(null);

  const stopSession = useCallback(async () => {
    setError("");
    setStatus("Stopping session...");

    try {
      if (sessionRef.current) {
        try {
          // Stop voice chat first if available
          if (sessionRef.current.voiceChat) {
            sessionRef.current.voiceChat.stop();
          }
        } catch (e) {
          // Ignore voice chat stop errors on cleanup
          console.warn("Error stopping voice chat:", e);
        }
        await sessionRef.current.stop();
        sessionRef.current = null;
      }
      setIsRunning(false);
      setIsListening(false);
      setStatus("Idle");
    } catch (e) {
      console.error("Error stopping LiveAvatar session:", e);
      setError(e.message || "Failed to stop LiveAvatar session.");
      setStatus("Error while stopping session.");
    }
  }, []);

  const startSession = useCallback(async () => {
    if (isStarting || isRunning) return;

    setIsStarting(true);
    setError("");
    setStatus("Requesting session token from HeyGen...");

    try {
      const token = await createLiveAvatarSessionToken();

      setStatus("Starting LiveAvatar session...");

      const userConfig = {
        voiceChat: true,
      };

      const liveAvatarSession = new LiveAvatarSession(token, userConfig);
      console.debug("LiveAvatarSession created, starting...");

      // Start the session (connect to LiveKit / WebRTC)
      await liveAvatarSession.start();
      console.debug("LiveAvatarSession started, state:", liveAvatarSession.state);

      // Attach media tracks to the <video> element AFTER session start
      if (videoRef.current) {
        videoRef.current.autoplay = true;
        videoRef.current.playsInline = true;
        // Start muted so autoplay is allowed; user can unmute via browser controls
        videoRef.current.muted = true;
        liveAvatarSession.attach(videoRef.current);
        console.debug("LiveAvatarSession attached to video element");
      }

      // Start voice chat (microphone + avatar audio)
      try {
        if (liveAvatarSession.voiceChat) {
          await liveAvatarSession.voiceChat.start();
          setIsListening(true);
          console.debug("VoiceChat started");
        }
      } catch (e) {
        console.error("Error starting voice chat:", e);
        setStatus("Session running, but mic/audio not active.");
      }

      sessionRef.current = liveAvatarSession;
      setIsRunning(true);
      setStatus("Session running. You can talk or send a message.");
    } catch (e) {
      console.error("Error starting LiveAvatar session:", e);
      setError(e.message || "Failed to start LiveAvatar session.");
      setStatus("Idle");
    } finally {
      setIsStarting(false);
    }
  }, [isRunning, isStarting]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (sessionRef.current) {
        sessionRef.current.stop().catch(() => {});
        sessionRef.current = null;
      }
      setIsListening(false);
    };
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        HeyGen LiveAvatar
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ maxWidth: 400, textAlign: "center" }}
      >
        Click &quot;Start Avatar&quot; to create a LiveAvatar session using your
        HeyGen account. This demo uses voice chat enabled by default.
      </Typography>

      <Box
        sx={{
          width: "100%",
          maxWidth: 480,
          height: 320,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          bgcolor: "black",
        }}
      >
        <video
          ref={videoRef}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </Box>

      {/* Text input to send a message to the avatar */}
      <Box
        sx={{
          mt: 2,
          width: "100%",
          maxWidth: 480,
          display: "flex",
          gap: 1,
        }}
      >
        <input
          type="text"
          placeholder="Type a message to the avatar..."
          value={textInput}
          onChange={(e) => setTextInput(e.target.value)}
          disabled={!isRunning}
          style={{
            flex: 1,
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #ccc",
            fontSize: 14,
          }}
        />
        <Button
          variant="contained"
          disabled={!isRunning || !textInput.trim()}
          onClick={() => {
            if (!sessionRef.current || !textInput.trim()) return;
            try {
              sessionRef.current.message(textInput.trim());
              setTextInput("");
              setStatus("Message sent to avatar.");
            } catch (e) {
              console.error("Error sending message:", e);
              setError(e.message || "Failed to send message to avatar.");
            }
          }}
        >
          Send
        </Button>
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          disabled={isStarting || isRunning}
          onClick={startSession}
        >
          {isStarting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Start Avatar"
          )}
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          disabled={!isRunning}
          onClick={stopSession}
        >
          Stop Avatar
        </Button>
        <Tooltip
          title={
            isListening
              ? "Avatar is listening to your mic"
              : "Click to enable mic for the avatar"
          }
        >
          <span>
            <IconButton
              color={isListening ? "primary" : "default"}
              disabled={!isRunning}
              onClick={() => {
                if (!sessionRef.current) return;
                try {
                  if (isListening) {
                    if (sessionRef.current.voiceChat) {
                      sessionRef.current.voiceChat.mute();
                    }
                    setIsListening(false);
                    setStatus("Session running. Mic is OFF.");
                  } else {
                    if (sessionRef.current.voiceChat) {
                      sessionRef.current.voiceChat.unmute();
                    }
                    setIsListening(true);
                    setStatus("Session running. Mic is ON and listening.");
                  }
                } catch (e) {
                  console.error("Error toggling listening:", e);
                  setError(
                    e.message || "Failed to toggle microphone listening state."
                  );
                }
              }}
            >
              {isListening ? <Mic /> : <MicOff />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Typography
        variant="body2"
        sx={{ color: error ? "error.main" : "text.secondary" }}
      >
        {error || status}
      </Typography>
    </Box>
  );
}

